/**
 * Onboarding Steps Configuration
 *
 * This file contains the configuration for all onboarding steps in the LaunchView.
 * Each step object defines the UI elements, behavior, and completion logic.
 */

export const stepsConfig = [
  {
    id: 'email-confirmation',
    title: 'Confirm your email address',
    description:
      'Check your email and click the verification link to activate your account.',
    buttonText: null, // No action button
    buttonIcon: null,
    summaryText: 'Email confirmed',
    editText: 'Email',
    action: 'confirmEmail',
    event: 'email_confirmed',
    isDialogOpen: false,
    help: {
      category: 'account',
      slug: 'email-verification',
    },
    isFinish: false,
    completed: false,
    homeModeOnly: true, // Only visible on home page
  },
  {
    id: 'files',
    title: 'Import Files',
    description:
      'Upload your training materials, documents, and resources. Our AI will analyze and organize them for optimal learning experiences. These files will be available to your invited contacts.',
    buttonText: 'Import Files',
    buttonIcon: 'mdi-upload',
    summaryText: 'Files imported',
    editText: 'Files',
    dialog: 'ImportFilesDialog',
    action: 'showDialog',
    event: 'imported_files',
    isDialogOpen: false,
    help: {
      category: 'files',
      slug: 'files',
    },
    isFinish: false,
    completed: false,
  },
  {
    id: 'contacts',
    title: 'Import Contacts',
    description:
      'Add your team members, students, or trainees. Import from CSV or connect your existing CRM system seamlessly.',
    buttonText: 'Import Contacts',
    buttonIcon: 'mdi-account-plus',
    summaryText: 'Contacts imported',
    editText: 'Contacts',
    dialog: 'ImportContactsDialog',
    action: 'showDialog',
    event: 'imported_contacts',
    isDialogOpen: false,
    help: {
      category: 'contacts',
      slug: 'importing-contacts',
    },
    isFinish: false,
    completed: false,
  },
  {
    id: 'invite-contacts',
    title: 'Invite Contacts',
    description:
      'Send invitations to your contacts so they can join and start engaging with their portal and searching content right away.',
    buttonText: 'Invite Contacts',
    buttonIcon: 'mdi-email',
    summaryText: 'Contacts invited',
    editText: 'Invitations',
    dialog: 'InviteContactsDialog',
    action: 'showDialog',
    event: 'invited_contacts',
    isDialogOpen: false,
    help: {
      category: 'contacts',
      slug: 'inviting-contacts',
    },
    isFinish: false,
    completed: false,
  },
  {
    id: 'sms',
    title: 'Setup SMS-Line',
    description:
      'Configure SMS notifications to keep your team engaged. Send reminders, updates, and learning prompts automatically.',
    buttonText: 'Setup SMS',
    buttonIcon: 'mdi-message-cog',
    summaryText: 'SMS-Line configured',
    editText: 'SMS-Line',
    dialog: 'SetupSmsLineDialog',
    action: 'showDialog',
    event: 'sms_line_set',
    isDialogOpen: false,
    help: {
      category: 'messaging',
      slug: 'messaging-faq',
    },
    isFinish: false,
    completed: false,
  },
  {
    id: 'finish',
    title: 'FINISH!',
    description:
      "Complete your onboarding and start using our AI-powered learning platform. You're all set to begin!",
    buttonText: 'Complete Setup',
    buttonIcon: 'mdi-check-circle',
    summaryText: 'Setup completed',
    summaryIcon: 'mdi-check-circle',
    editText: 'Setup',
    action: 'finishSetup',
    isFinish: true,
    completed: false,
  },
];

/**
 * Creates a deep copy of the steps configuration
 * This ensures each component instance has its own copy of the steps
 * @param {boolean} isSMSEnabled - Whether SMS functionality is enabled
 * @param {boolean} isHomeMode - Whether this is for home page (shows email confirmation step)
 * @param {boolean} isUserActivated - Whether the user's email is activated
 * @returns {Array} A deep copy of the steps configuration with appropriate filtering
 */
export function createStepsArray(
  isSMSEnabled = true,
  isHomeMode = false,
  isUserActivated = true
) {
  const steps = JSON.parse(JSON.stringify(stepsConfig));

  return steps.filter((step) => {
    // Filter out SMS step if SMS is not enabled
    if (step.id === 'sms' && !isSMSEnabled) {
      return false;
    }

    // Filter out email confirmation step if not in home mode or user is already activated
    if (step.id === 'email-confirmation' && (!isHomeMode || isUserActivated)) {
      return false;
    }

    // Filter out home-mode-only steps if not in home mode
    if (step.homeModeOnly && !isHomeMode) {
      return false;
    }

    return true;
  });
}
